// Company profile page (see `features/company-profile.md`), reached by opening
// a company from the Companies list. Shows the company's logo, name and
// markdown description, and — for user managers — the users linked to the
// company, with the same manage actions as the Users screen but scoped to this
// company.
//
// What the caller may do mirrors the server rules (which are enforced there on
// every request): renaming needs the platform-level CREATE_COMPANY permission,
// so a Company Admin can edit the profile (logo + description, via
// EDIT_COMPANY) but never the name; user actions need the corresponding
// CREATE_USER / EDIT_USER / DELETE_USER permissions, and a company-scoped
// manager only ever sees users of their own companies. Deleting a user is
// Super-Admin-only; a scoped manager unlinks the user from this company
// instead (the account stays on the platform).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import BusinessIcon from '@mui/icons-material/Business';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import type {
  CompanySeatUsage,
  LanguageCode,
  PermissionInfo,
  RoleInfo,
  UserAccount,
} from '../domain';
import {
  COMPANY_DESCRIPTION_MAX,
  COMPANY_NAME_MAX,
  DEFAULT_LANGUAGE,
  PLATFORM_PERMISSION_IDS,
  SUPER_ADMIN_ROLE_ID,
  companyGrantableRoles,
  isCompanyVerified,
  localizedLocationLabel,
} from '../domain';
import { LOCALES, LOCALE_META } from '../i18n/messages';
import {
  deleteUser,
  fetchCompanySeatUsage,
  fetchPermissions,
  fetchRoles,
  fetchUsers,
  updateUser,
  uploadLogo,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';
import { SeatUsageSummary } from './SeatUsageSummary';
import { AthletesSection } from './AthletesSection';
import { ClubGuestsSection } from './ClubGuestsSection';
import { AthleteProfileView } from './AthleteProfileView';
import { TeamsSection } from './TeamsSection';
import { TrainingClassesSection } from './TrainingClassesSection';
import { CompanyPhotosSection } from './CompanyPhotosSection';
import { publicClubUrl } from './public/clubSharePath';
import { CoverImagePicker } from './CoverImagePicker';
import { Markdown } from './Markdown';
import { UserDialog } from './UserDialog';

// Prefer the locale catalogue entry for a role name; unknown (custom) roles
// fall back to the server-provided English text (same pattern as UsersView).
function catalogText(
  t: (key: string) => string,
  key: string,
  fallback: string,
): string {
  const translated = t(key);
  return translated === key ? fallback : translated;
}

// The profile page's sections are split across tabs so the page stays light —
// only the active section mounts (and fetches). Athletes/teams/training need
// company access; users needs a user-management permission (see `canAct` /
// `showUsers` below), so those tabs appear conditionally.
type ProfileTab =
  | 'profile'
  | 'athletes'
  | 'teams'
  | 'training'
  | 'photos'
  | 'users';

interface CompanyProfileViewProps {
  companyId: number;
  /** Return to the Companies list (also used when the company disappears). */
  onBack: () => void;
}

export function CompanyProfileView({ companyId, onBack }: CompanyProfileViewProps) {
  const { companies, locations, renameCompany, updateCompanyProfile } =
    useTournament();
  const { me, hasPermission, refreshMe } = useAuth();
  const { t, locale } = useTranslation();

  const company = companies.find((c) => c.id === companyId) ?? null;
  // The company's home location (set at creation, immutable), for display only.
  const companyLocation =
    company && company.locationId !== null
      ? (locations.find((l) => l.id === company.locationId) ?? null)
      : null;

  // What the signed-in user may do here (presentation only — the server
  // enforces the same rules on every request).
  const canAct =
    me !== null && (me.allCompanies || me.user.companyIds.includes(companyId));
  const canRename = canAct && hasPermission('CREATE_COMPANY');
  const canEditProfile = canAct && hasPermission('EDIT_COMPANY');
  const canCreateUser = canAct && hasPermission('CREATE_USER');
  const canEditUser = canAct && hasPermission('EDIT_USER');
  // Deleting a user is Super-Admin-only (the server refuses a company-scoped
  // delete); a scoped manager unlinks the user from this company instead —
  // the account stays on the platform.
  const canDeleteUser =
    canAct && hasPermission('DELETE_USER') && me?.allCompanies === true;
  const canUnlinkUser = canEditUser && me?.allCompanies === false;
  // The user list endpoint is open to anyone holding a user-management
  // permission; without one the section is hidden entirely.
  const showUsers = canCreateUser || canEditUser || canDeleteUser;

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Which section is on screen. Defaults to the always-present profile tab.
  const [tab, setTab] = useState<ProfileTab>('profile');

  // When set, an athlete's full profile page is open (drilled in from the
  // Athletes tab), replacing the company page until dismissed.
  const [openProfileNumber, setOpenProfileNumber] = useState<string | null>(null);

  // Inline rename (Super Admin / platform company managers only).
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  // Profile edit mode: markdown draft + logo pick. `logoBlob` is a freshly
  // picked (already cropped) image; `keepLogo` distinguishes "leave the stored
  // logo alone" from "remove it" when no new image was picked.
  const [editingProfile, setEditingProfile] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [languageDraft, setLanguageDraft] = useState<LanguageCode>(DEFAULT_LANGUAGE);
  const [logoBlob, setLogoBlob] = useState<Blob | null>(null);
  const [keepLogo, setKeepLogo] = useState(true);

  // Whether the public home-page share link was just copied to the clipboard
  // (drives the transient "Copied!" tooltip).
  const [copiedHomeLink, setCopiedHomeLink] = useState(false);

  // Users of this company (user managers only).
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [permissions, setPermissions] = useState<PermissionInfo[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserAccount | null>(null);
  const [pendingDelete, setPendingDelete] = useState<UserAccount | null>(null);
  const [pendingUnlink, setPendingUnlink] = useState<UserAccount | null>(null);

  // The data loaders below must not re-run when the language changes (their
  // effects would refetch and flash the view), so they read the translator
  // through a ref that always holds the latest `t` without changing identity.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // Subscription seat usage for this club (see `features/subscription-seats.md`).
  // Fetched per company rather than read from the context list so the figure is
  // live: the roster is edited on the other tabs of this very page.
  const [seats, setSeats] = useState<CompanySeatUsage | null>(null);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const [nextUsers, nextRoles, nextPermissions] = await Promise.all([
        fetchUsers(),
        fetchRoles(),
        fetchPermissions(),
      ]);
      setUsers(nextUsers);
      setRoles(nextRoles);
      setPermissions(nextPermissions);
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : tRef.current('admin.companies.loadUsersFailed'),
      );
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Seat usage is shown on the profile tab; reload whenever it is (re-)opened, so
  // returning from the athletes tab shows the count the visit just changed.
  useEffect(() => {
    if (tab !== 'profile') return;
    let cancelled = false;
    void (async () => {
      try {
        const usage = await fetchCompanySeatUsage(companyId);
        if (!cancelled) setSeats(usage);
      } catch {
        // A seat count is context, not the point of the page: leave the previous
        // figure (or nothing) rather than covering the profile with an error.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, tab]);

  // Load the user list only once its tab is open (each section fetches lazily,
  // keeping the initial page light); reloads whenever the tab is revisited.
  useEffect(() => {
    if (showUsers && tab === 'users') void loadUsers();
  }, [showUsers, tab, loadUsers]);

  // If the active section stops being available (a manager can edit their own
  // access away), fall back to the always-present profile tab so no orphaned
  // tab value is left selected.
  useEffect(() => {
    if (tab === 'users' && !showUsers) setTab('profile');
    else if (tab !== 'profile' && tab !== 'users' && !canAct) setTab('profile');
  }, [tab, showUsers, canAct]);

  // The company can vanish underneath the page (deleted elsewhere, or access
  // revoked and the refreshed list no longer carries it): drop back to the list.
  useEffect(() => {
    if (companies.length > 0 && !companies.some((c) => c.id === companyId)) {
      onBack();
    }
  }, [companies, companyId, onBack]);

  const companyUsers = useMemo(
    () => users.filter((user) => user.companyIds.includes(companyId)),
    [users, companyId],
  );
  const roleNames = useMemo(
    () => new Map(roles.map((role) => [role.id, role.name])),
    [roles],
  );
  // A company-scoped manager is offered only the roles a club hands out —
  // Company Admin and Coach (no Super Admin, no hand-assigned Athlete; see
  // `companyGrantableRoles`).
  const grantableRoles = useMemo(
    () => (me?.allCompanies ? roles : companyGrantableRoles(roles)),
    [roles, me],
  );
  const grantablePermissions = useMemo(
    () =>
      me?.allCompanies
        ? permissions
        : permissions.filter((p) => !PLATFORM_PERMISSION_IDS.includes(p.id)),
    [permissions, me],
  );
  const initialCompanyIds = useMemo(() => [companyId], [companyId]);

  // Absolute share URL for the club's public home page (built from the immutable,
  // unguessable home token — never the company id).
  const homeShareUrl = useMemo(
    () => (company ? publicClubUrl(company.homeToken, window.location.origin) : ''),
    [company],
  );

  const runAction = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.common.requestFailed'));
    } finally {
      setBusy(false);
    }
  };

  const submitRename = async () => {
    const name = nameDraft.trim();
    if (!company || !name || busy) return;
    await runAction(async () => {
      await renameCompany(company.id, name);
      setEditingName(false);
    });
  };

  const startProfileEdit = () => {
    if (!company) return;
    setDescriptionDraft(company.description);
    setLanguageDraft(company.defaultLanguage);
    setLogoBlob(null);
    setKeepLogo(true);
    setEditingProfile(true);
  };

  const submitProfile = async () => {
    if (!company || busy) return;
    await runAction(async () => {
      // A freshly picked logo is uploaded first (same mechanism as tournament
      // covers); otherwise the stored URL is kept or cleared as chosen.
      const logoUrl = logoBlob
        ? await uploadLogo(logoBlob)
        : keepLogo
          ? company.logoUrl
          : null;
      await updateCompanyProfile(company.id, {
        description: descriptionDraft,
        logoUrl,
        defaultLanguage: languageDraft,
      });
      setEditingProfile(false);
    });
  };

  // Flip the club home page's tournaments-listing preference (the page itself is
  // always public). Gated by EDIT_COMPANY in the UI and on the server.
  const toggleHome = (patch: { showTournaments?: boolean }) =>
    runAction(async () => {
      if (!company) return;
      await updateCompanyProfile(company.id, patch);
    });

  const copyHomeLink = async () => {
    try {
      await navigator.clipboard.writeText(homeShareUrl);
      setCopiedHomeLink(true);
      window.setTimeout(() => setCopiedHomeLink(false), 2000);
    } catch {
      // Clipboard blocked (e.g. insecure context); the link stays selectable.
    }
  };

  // After a save that may concern the signed-in user, refresh both the list
  // and the caller's own access picture (their buttons may change).
  const afterUserChange = useCallback(
    async (touchedSelf: boolean) => {
      await loadUsers();
      if (touchedSelf) await refreshMe();
    },
    [loadUsers, refreshMe],
  );

  const toggleBlocked = (user: UserAccount) =>
    runAction(async () => {
      await updateUser(user.id, { blocked: user.blockedAt === null });
      await afterUserChange(user.id === me?.user.id);
    });

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    await runAction(async () => {
      setUsers(await deleteUser(target.id));
    });
  };

  // Remove this company from the target's links, leaving links to any other
  // companies untouched. The account itself stays — only a Super Admin can
  // delete it.
  const confirmUnlink = async () => {
    if (!pendingUnlink) return;
    const target = pendingUnlink;
    setPendingUnlink(null);
    await runAction(async () => {
      await updateUser(target.id, {
        companyIds: target.companyIds.filter((id) => id !== companyId),
      });
      await afterUserChange(target.id === me?.user.id);
    });
  };

  if (!company) {
    return (
      <Box sx={{ maxWidth: 880, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          {t('admin.companies.profile.allCompanies')}
        </Button>
        <Paper variant="outlined" sx={{ p: 4, mt: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('admin.companies.profile.notAvailable')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Drilled into a single athlete/coach profile from the Athletes tab.
  if (openProfileNumber !== null) {
    return (
      <AthleteProfileView
        personalNumber={openProfileNumber}
        onBack={() => setOpenProfileNumber(null)}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        {t('admin.companies.profile.allCompanies')}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* A self-registered club awaits the platform's review: until a Super
          Admin verifies it, athletes, coaches and users cannot be added (see
          `features/company-registration.md`). */}
      {!isCompanyVerified(company) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('admin.companies.profile.unverifiedNotice')}
        </Alert>
      )}

      {/* Header: logo, name (inline-renamable with the platform permission). */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar
          variant="rounded"
          src={company.logoUrl ?? undefined}
          alt={t('admin.companies.profile.logoAlt', { name: company.name })}
          sx={{ width: 72, height: 72 }}
        >
          <BusinessIcon fontSize="large" />
        </Avatar>
        {editingName ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitRename();
              }}
              slotProps={{ htmlInput: { maxLength: COMPANY_NAME_MAX } }}
            />
            <IconButton
              aria-label={t('admin.companies.profile.saveNameAria')}
              disabled={busy || nameDraft.trim().length === 0}
              onClick={() => void submitRename()}
            >
              <CheckIcon />
            </IconButton>
            <IconButton
              aria-label={t('admin.companies.profile.cancelRenameAria')}
              onClick={() => setEditingName(false)}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        ) : (
          <>
            <Typography variant="h5" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
              {company.name}
            </Typography>
            {canRename && (
              <Tooltip title={t('admin.companies.profile.renameCompany')}>
                <IconButton
                  aria-label={t('admin.companies.profile.renameAria', {
                    name: company.name,
                  })}
                  disabled={busy}
                  onClick={() => {
                    setNameDraft(company.name);
                    setEditingName(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, value: ProfileTab) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab value="profile" label={t('admin.companies.profile.tabs.profile')} />
        {canAct && (
          <Tab
            value="athletes"
            label={t('admin.companies.profile.tabs.athletes')}
          />
        )}
        {canAct && (
          <Tab value="teams" label={t('admin.companies.profile.tabs.teams')} />
        )}
        {canAct && (
          <Tab
            value="training"
            label={t('admin.companies.profile.tabs.training')}
          />
        )}
        {canAct && (
          <Tab value="photos" label={t('admin.companies.profile.tabs.photos')} />
        )}
        {showUsers && (
          <Tab value="users" label={t('admin.companies.profile.tabs.users')} />
        )}
      </Tabs>

      {/* The plan-relevant headcount, above the profile text: it is the first
          thing a club admin (and a Super Admin auditing one) comes here for. */}
      {tab === 'profile' && seats && <SeatUsageSummary usage={seats} />}

      {/* Profile: markdown description + logo, editable with EDIT_COMPANY. */}
      {tab === 'profile' && (
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('admin.companies.profile.about')}
          </Typography>
          {canEditProfile && !editingProfile && (
            <Button
              size="small"
              startIcon={<EditIcon />}
              disabled={busy}
              onClick={startProfileEdit}
            >
              {t('admin.companies.profile.editProfile')}
            </Button>
          )}
        </Stack>

        {editingProfile ? (
          <Stack spacing={2}>
            <CoverImagePicker
              blob={logoBlob}
              existingUrl={keepLogo ? company.logoUrl : null}
              disabled={busy}
              label={t('admin.companies.profile.logoLabel')}
              caption={t('admin.companies.profile.logoCaption')}
              onChange={(blob) => {
                setLogoBlob(blob);
                setKeepLogo(false);
              }}
            />
            <TextField
              label={t('admin.companies.profile.description')}
              fullWidth
              multiline
              minRows={6}
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              helperText={t('admin.companies.profile.descriptionHelp', {
                max: COMPANY_DESCRIPTION_MAX,
              })}
              slotProps={{ htmlInput: { maxLength: COMPANY_DESCRIPTION_MAX } }}
            />
            <TextField
              select
              label={t('admin.companies.profile.defaultLanguage')}
              value={languageDraft}
              onChange={(e) => setLanguageDraft(e.target.value as LanguageCode)}
              helperText={t('admin.companies.profile.defaultLanguageHelp')}
              sx={{ maxWidth: 320 }}
            >
              {LOCALES.map((code) => (
                <MenuItem key={code} value={code}>
                  {LOCALE_META[code].label}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button disabled={busy} onClick={() => setEditingProfile(false)}>
                {t('admin.common.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => void submitProfile()}
              >
                {t('admin.companies.profile.saveProfile')}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <>
            {company.description ? (
              <Markdown>{company.description}</Markdown>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(
                  canEditProfile
                    ? 'admin.companies.profile.noDescriptionEditable'
                    : 'admin.companies.profile.noDescription',
                )}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
            >
              <Chip
                size="small"
                variant="outlined"
                label={t('admin.companies.profile.typeChip', {
                  type: t(`admin.companies.types.${company.type}`),
                })}
              />
              <Chip
                size="small"
                variant="outlined"
                label={t('admin.companies.profile.locationChip', {
                  location: companyLocation
                    ? localizedLocationLabel(companyLocation, locale)
                    : t('admin.companies.profile.noLocation'),
                })}
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2 }}
            >
              {t('admin.companies.profile.defaultLanguageCaption', {
                label: LOCALE_META[company.defaultLanguage].label,
              })}
            </Typography>
          </>
        )}
      </Paper>
      )}

      {/* Public home page: the shareable club page (its link + the tournaments
          preference). Every company's page is public, so there is no visibility
          toggle. Visible to anyone with company access; the preference needs
          EDIT_COMPANY (mirrors the server). */}
      {tab === 'profile' && canAct && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {t('admin.companies.profile.home.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.companies.profile.home.description')}
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={company.showTournaments}
                  disabled={busy || !canEditProfile}
                  onChange={(e) =>
                    void toggleHome({ showTournaments: e.target.checked })
                  }
                />
              }
              label={t('admin.companies.profile.home.showTournaments')}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              value={homeShareUrl}
              label={t('admin.companies.profile.home.shareLink')}
              slotProps={{ htmlInput: { readOnly: true } }}
              onFocus={(e) => e.target.select()}
            />
            <Tooltip
              title={
                copiedHomeLink
                  ? t('admin.companies.profile.home.copied')
                  : t('admin.companies.profile.home.copyLink')
              }
            >
              <IconButton
                aria-label={t('admin.companies.profile.home.copyLink')}
                onClick={() => void copyHomeLink()}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('admin.companies.profile.home.openLink')}>
              <span>
                <IconButton
                  aria-label={t('admin.companies.profile.home.openLink')}
                  component="a"
                  href={homeShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      {/* Athletes & coaches of this company (company members; managers edit). */}
      {tab === 'athletes' && canAct && (
        <>
          <AthletesSection companyId={companyId} onOpenProfile={setOpenProfileNumber} />
          {/* Athletes of other clubs who also train here (see
              `features/athlete-clubs.md`) — on the roster, but not owned. */}
          <ClubGuestsSection companyId={companyId} />
        </>
      )}

      {/* Teams of this company, per sport, with their members. */}
      {tab === 'teams' && canAct && <TeamsSection companyId={companyId} />}

      {/* Training classes of this company: coaches, enrolments and attendance. */}
      {tab === 'training' && canAct && (
        <TrainingClassesSection companyId={companyId} />
      )}

      {/* Company profile photos (company access; MANAGE_PHOTOS to add/remove). */}
      {tab === 'photos' && canAct && (
        <CompanyPhotosSection companyId={companyId} />
      )}

      {/* Users of this company (user managers only). */}
      {tab === 'users' && showUsers && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {t('admin.companies.profile.users')}
            </Typography>
            {canCreateUser && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={busy}
                onClick={() => {
                  setEditingUser(null);
                  setDialogOpen(true);
                }}
              >
                {t('admin.companies.profile.addUser')}
              </Button>
            )}
          </Stack>

          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : companyUsers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('admin.companies.profile.noUsers')}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {companyUsers.map((user) => {
                const self = user.id === me?.user.id;
                const blocked = user.blockedAt !== null;
                return (
                  <Paper
                    key={user.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      opacity: blocked ? 0.6 : 1,
                    }}
                  >
                    <PersonIcon color={blocked ? 'disabled' : 'primary'} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" noWrap title={user.email}>
                          {user.name || user.email}
                        </Typography>
                        {self && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('admin.common.you')}
                          />
                        )}
                        {blocked && (
                          <Chip
                            size="small"
                            color="error"
                            label={t('admin.companies.profile.blocked')}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {user.email}
                      </Typography>
                    </Box>
                    {user.roleIds.map((roleId) => (
                      <Chip
                        key={roleId}
                        size="small"
                        color={roleId === SUPER_ADMIN_ROLE_ID ? 'secondary' : 'default'}
                        label={catalogText(
                          t,
                          `admin.access.roles.${roleId}.name`,
                          roleNames.get(roleId) ?? roleId,
                        )}
                      />
                    ))}
                    {canEditUser && (
                      <Tooltip
                        title={t('admin.companies.profile.editUser', {
                          email: user.email,
                        })}
                      >
                        <span>
                          <IconButton
                            aria-label={t('admin.companies.profile.editUser', {
                              email: user.email,
                            })}
                            disabled={busy}
                            onClick={() => {
                              setEditingUser(user);
                              setDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canEditUser && (
                      <Tooltip
                        title={
                          self
                            ? t('admin.companies.profile.cannotBlockSelf')
                            : blocked
                              ? t('admin.companies.profile.unblockUser', {
                                  email: user.email,
                                })
                              : t('admin.companies.profile.blockUser', {
                                  email: user.email,
                                })
                        }
                      >
                        <span>
                          <IconButton
                            aria-label={
                              blocked
                                ? t('admin.companies.profile.unblockUser', {
                                    email: user.email,
                                  })
                                : t('admin.companies.profile.blockUser', {
                                    email: user.email,
                                  })
                            }
                            disabled={busy || (self && !blocked)}
                            onClick={() => void toggleBlocked(user)}
                          >
                            {blocked ? (
                              <LockOpenIcon fontSize="small" />
                            ) : (
                              <BlockIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canUnlinkUser && (
                      <Tooltip
                        title={
                          self
                            ? t('admin.companies.profile.cannotUnlinkSelf')
                            : t('admin.companies.profile.unlinkUser', {
                                email: user.email,
                              })
                        }
                      >
                        <span>
                          <IconButton
                            aria-label={t(
                              'admin.companies.profile.unlinkUser',
                              { email: user.email },
                            )}
                            disabled={busy || self}
                            onClick={() => setPendingUnlink(user)}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canDeleteUser && (
                      <Tooltip
                        title={
                          self
                            ? t('admin.companies.profile.cannotDeleteSelf')
                            : ''
                        }
                      >
                        <span>
                          <IconButton
                            aria-label={t(
                              'admin.companies.profile.deleteUser',
                              { email: user.email },
                            )}
                            disabled={busy || self}
                            onClick={() => setPendingDelete(user)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}

          <UserDialog
            open={dialogOpen}
            user={editingUser}
            roles={grantableRoles}
            permissions={grantablePermissions}
            companies={companies}
            initialCompanyIds={initialCompanyIds}
            onClose={() => setDialogOpen(false)}
            onSaved={(saved) => void afterUserChange(saved.id === me?.user.id)}
          />

          <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
            <DialogTitle>{t('admin.companies.profile.deleteDialog.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {pendingDelete
                  ? t('admin.companies.profile.deleteDialog.body', {
                      email: pendingDelete.email,
                    })
                  : ''}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingDelete(null)}>
                {t('admin.common.cancel')}
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => void confirmDelete()}
              >
                {t('admin.common.delete')}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={pendingUnlink !== null}
            onClose={() => setPendingUnlink(null)}
          >
            <DialogTitle>
              {t('admin.companies.profile.unlinkDialog.title')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {pendingUnlink
                  ? t('admin.companies.profile.unlinkDialog.body', {
                      email: pendingUnlink.email,
                    })
                  : ''}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingUnlink(null)}>
                {t('admin.common.cancel')}
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => void confirmUnlink()}
              >
                {t('admin.companies.profile.unlinkDialog.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}
    </Box>
  );
}
